import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Icon from '../../../atoms/Icon'
import { FILTER_PRESETS, FILTER_PRESETS_BY_KEY } from '../lib/scopeFilterPredicates'
import FilterBuilderRow from './FilterBuilderRow'

/**
 * Recursive node renderer for the scope-filter tree. A filter-collection
 * is rendered as:
 *   - a method toggle ([All of] | [Any of])
 *   - a list of children, each either:
 *       - a predicate row (FilterBuilderRow)
 *       - another nested collection (recursive call)
 *       - a preset chip (rendered inline)
 *   - an action row to add predicates / groups / presets
 *
 * `isRoot` styling distinguishes the top-level collection (no border-left
 * accent) from nested collections.
 */
export default function FilterBuilderCollection ({ tree, onChange, isRoot = false, classes }) {
  // Memoize the derived defaults so they don't churn the useCallback deps
  // below on every parent render. `tree.filters || []` would otherwise be
  // a fresh array reference each render, invalidating every callback's
  // memoization (and re-running the no-op effects in deeply nested
  // FilterBuilder trees).
  const filters = useMemo(() => tree.filters || [], [tree.filters])
  const method = tree.method || 'All'

  const setMethod = useCallback((next) => {
    onChange({ ...tree, method: next })
  }, [tree, onChange])

  const updateChild = useCallback((index, nextChild) => {
    const next = [...filters]
    if (nextChild == null) next.splice(index, 1)
    else next[index] = nextChild
    onChange({ ...tree, filters: next })
  }, [tree, filters, onChange])

  const addPredicate = useCallback(() => {
    // Default predicate: blank string-equal on a placeholder path. The
    // user picks the actual path / operator from the row's controls.
    onChange({
      ...tree,
      filters: [
        ...filters,
        { $type: 'string-equal', path: '', haystack: [] }
      ]
    })
  }, [tree, filters, onChange])

  const addGroup = useCallback(() => {
    onChange({
      ...tree,
      filters: [
        ...filters,
        { $type: 'filter-collection', method: 'Any', filters: [] }
      ]
    })
  }, [tree, filters, onChange])

  const addPreset = useCallback((presetKey) => {
    onChange({
      ...tree,
      filters: [...filters, { $type: 'preset', preset: presetKey }]
    })
  }, [tree, filters, onChange])

  return (
    <div className={clsx(classes.collection, { [classes.collectionNested]: !isRoot })}>
      <div className={classes.collectionHeader}>
        <div className={classes.methodToggle} role='group' aria-label='Filter combination method'>
          <span
            className={clsx(classes.methodOption, { [classes.methodOptionActive]: method === 'All' })}
            onClick={() => setMethod('All')}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMethod('All') } }}
          >
            All of
          </span>
          <span
            className={clsx(classes.methodOption, { [classes.methodOptionActive]: method === 'Any' })}
            onClick={() => setMethod('Any')}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMethod('Any') } }}
          >
            Any of
          </span>
        </div>
        {!isRoot && (
          <span
            className={classes.removeBtn}
            onClick={() => onChange(null)}
            role='button'
            tabIndex={0}
            aria-label='Remove group'
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(null) } }}
          >
            <Icon name='close' customSize={14} />
          </span>
        )}
      </div>

      {filters.length === 0 && (
        <div className={classes.emptyHint}>No filters yet -- add a predicate or preset below.</div>
      )}

      {filters.map((child, index) => {
        if (child.$type === 'filter-collection') {
          return (
            <FilterBuilderCollection
              key={index}
              tree={child}
              onChange={(next) => updateChild(index, next)}
              classes={classes}
            />
          )
        }
        if (child.$type === 'preset') {
          const meta = FILTER_PRESETS_BY_KEY[child.preset]
          return (
            <div key={index} className={classes.predicateRow}>
              <span className={classes.presetChip}>
                {meta?.label || `Preset: ${child.preset}`}
                <span
                  className={classes.removeBtn}
                  onClick={() => updateChild(index, null)}
                  role='button'
                  tabIndex={0}
                  aria-label='Remove preset'
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); updateChild(index, null) } }}
                >
                  <Icon name='close' customSize={12} />
                </span>
              </span>
            </div>
          )
        }
        return (
          <FilterBuilderRow
            key={index}
            predicate={child}
            onChange={(next) => updateChild(index, next)}
            classes={classes}
          />
        )
      })}

      <div className={classes.addRow}>
        <button type='button' className={classes.addBtn} onClick={addPredicate}>+ Add predicate</button>
        <button type='button' className={classes.addBtn} onClick={addGroup}>+ Add group</button>
        {FILTER_PRESETS.map(p => (
          <button
            key={p.preset}
            type='button'
            className={classes.addBtn}
            onClick={() => addPreset(p.preset)}
            title={p.description}
          >
            + {p.label}
          </button>
        ))}
      </div>
    </div>
  )
}

FilterBuilderCollection.propTypes = {
  tree: PropTypes.shape({
    $type: PropTypes.string.isRequired,
    method: PropTypes.oneOf(['All', 'Any']),
    filters: PropTypes.array
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  isRoot: PropTypes.bool,
  classes: PropTypes.object.isRequired
}
